import { FC, memo, useCallback } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import {
  ButtonGroup,
  Flex,
  IconButton,
  Pagination,
  PaginationPageChangeDetails,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { MetaPaginationProps } from './types/MetaPaginationProps';

const MetaPaginationComponent: FC<MetaPaginationProps> = ({
  setPage,
  page,
  meta,
  ...props
}) => {
  const isLoading = page !== meta?.currentPage;
  const handlePageChange = useCallback(
    ({ page: newPage }: PaginationPageChangeDetails) => {
      if (isLoading) return;
      setPage(newPage);
    },
    [setPage, isLoading],
  );

  if (!meta) return null;

  return (
    <Pagination.Root
      asChild
      colorPalette="brandSecondary"
      count={meta.total}
      defaultPage={1}
      onPageChange={handlePageChange}
      page={page}
      pageSize={meta.perPage}
    >
      <Flex {...props} alignItems="center" justifyContent="space-between">
        <Flex alignItems="center">
          <Text textStyle="sm">Displaying&nbsp;</Text>
          <Pagination.PageText flex="1" format="long" textStyle="sm" />
        </Flex>
        <ButtonGroup gap={1} size="sm" variant="outline">
          <Pagination.PrevTrigger asChild disabled={isLoading}>
            <IconButton borderRadius="lg">
              {isLoading ? (
                <Spinner colorPalette="colorPalette" />
              ) : (
                <LuChevronLeft />
              )}
            </IconButton>
          </Pagination.PrevTrigger>
          <Pagination.PageText />
          <Pagination.NextTrigger asChild disabled={isLoading}>
            <IconButton borderRadius="lg">
              {isLoading ? (
                <Spinner colorPalette="colorPalette" />
              ) : (
                <LuChevronRight />
              )}
            </IconButton>
          </Pagination.NextTrigger>
        </ButtonGroup>
      </Flex>
    </Pagination.Root>
  );
};

export const MetaPagination = memo(MetaPaginationComponent);
